import type { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { apiClient } from "app/modules/api-client/apiClient";
import {
    ALL_CAMPAIGNS_QUERY_PREFIX,
    RankTrackerQueryType,
} from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import { useRankTrackerQuery } from "app/pages/digital-marketing/keywords-campaigns/shared/hooks/useRankTrackerQuery";
import { createCampaignQueryKey } from "app/pages/digital-marketing/keywords-campaigns/shared/utils";

import { handleGscThrowOnError } from "../gscProblemErrors";
import type { GscCountry } from "../types";

type Params = {
    connectionId: number;
    site: string;
    startDate: string;
    endDate: string;
};

type QueryOptions = Omit<UseQueryOptions<GscCountry[]>, "queryKey" | "queryFn">;

const fetchGscCountries = async (params: Params): Promise<GscCountry[]> => {
    return apiClient.RankTracker.rtGetGscCountries({
        connectionId: params.connectionId,
        siteUrl: params.site,
        startDate: params.startDate,
        endDate: params.endDate,
    });
};

/** Fetches countries with click data for a GSC property within the given date range. */
export const useGscCountriesQuery = (params: Params, options?: QueryOptions) => {
    const queryKey: QueryKey = createCampaignQueryKey(ALL_CAMPAIGNS_QUERY_PREFIX, RankTrackerQueryType.GscCountries, [
        params.connectionId,
        params.site,
        params.startDate,
        params.endDate,
    ]);

    return useRankTrackerQuery({
        queryKey,
        queryFn: () => fetchGscCountries(params),
        throwOnError: handleGscThrowOnError("Error fetching GSC countries"),
        ...options,
    });
};
